import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useAuth } from '../context/AuthContext';
import {
  ShieldCheck, Droplets, Smartphone, Sparkles, ArrowRight,
  Zap, Users, Heart, BatteryCharging, WifiOff, MapPin,
  Check, X, Star, Quote, PawPrint,
} from 'lucide-react';

/* -------------------------------------------------------------------------- */
/*  Assets                                                                    */
/* -------------------------------------------------------------------------- */

const TAG_IMAGE =
  'https://customer-assets-agu9un31.emergentagent.net/job_nfc-pet-tags/artifacts/e7iql5ci_1785609256465.webp';

// Cinematic imagery (Unsplash CDN — royalty-free, cached, fast)
const IMG = {
  hero:
    'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=2400&q=85&auto=format&fit=crop',
  emotional:
    'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=1600&q=85&auto=format&fit=crop',
  founder:
    'https://images.unsplash.com/photo-1605568427561-40dd23c2acea?w=1600&q=85&auto=format&fit=crop',
  finalCta:
    'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=2400&q=85&auto=format&fit=crop',
  community:
    'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=1600&q=85&auto=format&fit=crop',
};

/* -------------------------------------------------------------------------- */
/*  Content                                                                   */
/* -------------------------------------------------------------------------- */

const trustChips = [
  { icon: WifiOff,    label: 'No App Required' },
  { icon: Droplets,   label: 'Waterproof' },
  { icon: Smartphone, label: 'Works with Modern Smartphones' },
  { icon: MapPin,     label: 'Designed in the UK' },
];

const howItWorks = [
  { step: '01', title: 'Tap or Scan',            body: 'The finder taps the tag with their phone, or scans the QR code — no app needed.' },
  { step: '02', title: 'View Pet Profile',       body: "They see your pet's photo, name and any important info in seconds." },
  { step: '03', title: 'Instantly Contact You',  body: 'One tap to call, WhatsApp or share a live location — no forms, no waiting.' },
  { step: '04', title: 'Your Pet Comes Home',    body: 'You reunite quickly, before worry has a chance to set in.' },
];

const benefits = [
  { icon: Zap,             title: 'Instant Owner Contact',      body: 'Whoever finds your pet can reach you in one tap. No account, no app, no friction.' },
  { icon: Heart,           title: 'Emergency Medical Info',     body: 'Share allergies, medication or conditions so your pet gets the right care immediately.' },
  { icon: Users,           title: 'Multiple Emergency Contacts',body: 'List family or friends so someone is always reachable, even when you can’t answer.' },
  { icon: MapPin,          title: 'Lost Pet Mode',              body: 'Flip a switch to alert finders, offer a reward and receive sighting locations.' },
  { icon: Droplets,        title: 'Waterproof',                 body: 'Muddy walks, splash pools, downpours — the tag keeps working when your pet needs it most.' },
  { icon: BatteryCharging, title: 'No Batteries. Ever.',        body: 'Powered by the finder’s phone. Nothing to charge, replace or forget.' },
  { icon: WifiOff,         title: 'No App Required',            body: 'Works on any modern iPhone or Android — even for people who’ve never heard of NFC.' },
  { icon: ShieldCheck,     title: 'Private by Design',          body: 'You choose what’s public. Full control, always, from your dashboard.' },
];

const comparison = [
  { label: 'Contact owner with one tap',   traditional: false, saferpaws: true  },
  { label: 'Photo & profile of your pet',  traditional: false, saferpaws: true  },
  { label: 'Emergency medical info',       traditional: false, saferpaws: true  },
  { label: 'Update details anytime',       traditional: false, saferpaws: true  },
  { label: 'Lost Pet Mode & sightings',    traditional: false, saferpaws: true  },
  { label: 'Works if your pet is chipped', traditional: true,  saferpaws: true  },
  { label: 'Requires a vet visit to read', traditional: true,  saferpaws: false },
  { label: 'Engraved information gets worn or outdated', traditional: true, saferpaws: false },
];

const faqs = [
  {
    q: 'Do I need an app to use SaferPaws?',
    a: 'No. Anyone with a modern smartphone can tap the tag or scan the QR code — the pet profile opens in their browser instantly. No downloads, no accounts.',
  },
  {
    q: 'Does it work if my pet is already microchipped?',
    a: 'Absolutely. A microchip needs a vet visit to be read. SaferPaws works the moment a stranger finds your pet, so they can reach you in seconds — long before a vet is involved.',
  },
  {
    q: 'Is the tag really waterproof?',
    a: 'Yes. Every SaferPaws tag is sealed and tested for rain, mud, swimming and everyday adventures.',
  },
  {
    q: 'Does it need charging?',
    a: 'Never. The tag is powered by the finder’s phone, so there’s nothing to charge, replace or forget.',
  },
  {
    q: 'Can I update my details after buying?',
    a: 'Yes. Log into your dashboard any time to update contact info, medical notes, photos or switch on Lost Pet Mode.',
  },
  {
    q: 'What if my pet loses the tag?',
    a: 'Just log in, deactivate the missing tag and order a replacement. Your pet profile stays exactly as you left it.',
  },
  {
    q: 'Is my information private?',
    a: 'You choose what’s visible on the public profile. Sensitive contact details are only revealed via the tap-to-call and message buttons — no phone numbers are shown on screen.',
  },
];

/* -------------------------------------------------------------------------- */
/*  Reusable UI                                                               */
/* -------------------------------------------------------------------------- */

function OrangeRule() {
  return (
    <div className="h-1 w-16 rounded-full bg-primary" aria-hidden="true" />
  );
}

function SectionEyebrow({ children }) {
  return (
    <span
      className="inline-block text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase text-primary"
    >
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function HomePage() {
  const { user } = useAuth();

  // SEO — populate title & meta on mount without adding react-helmet.
  useEffect(() => {
    document.title = 'SaferPaws — Smart Pet Tags to Bring Lost Pets Home';
    const setMeta = (name, content, attr = 'name') => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta(
      'description',
      'SaferPaws NFC pet tags help lost pets get home faster. Tap or scan to instantly contact the owner — no app required. Designed in the UK.'
    );
    setMeta('og:title', 'SaferPaws — Smart Pet Tags to Bring Lost Pets Home', 'property');
    setMeta(
      'og:description',
      'Peace of mind for pet owners. Tap or scan — one moment can bring your best friend home.',
      'property'
    );
    setMeta('og:type', 'website', 'property');
    setMeta('og:image', TAG_IMAGE, 'property');
    setMeta('twitter:card', 'summary_large_image');

    // JSON-LD structured data
    const ldId = 'saferpaws-jsonld';
    document.getElementById(ldId)?.remove();
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = ldId;
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'SaferPaws NFC Pet Tag',
      description:
        'Smart NFC pet tag with QR code. Tap or scan to instantly reunite lost pets with their owner. Waterproof, no app required.',
      image: TAG_IMAGE,
      brand: { '@type': 'Brand', name: 'SaferPaws' },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'GBP',
        price: '19.99',
        availability: 'https://schema.org/InStock',
      },
    });
    document.head.appendChild(ld);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      {/* ================================================================== */}
      {/* HERO                                                              */}
      {/* ================================================================== */}
      <section
        className="relative isolate min-h-[100svh] flex items-center pt-24 pb-16 overflow-hidden"
        data-testid="hero-section"
      >
        <img
          src={IMG.hero}
          alt="A dog running happily through a sunlit field with its owner"
          className="absolute inset-0 -z-10 w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        {/* dark gradient overlay */}
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'linear-gradient(90deg, rgba(10,10,10,0.94) 0%, rgba(10,10,10,0.75) 45%, rgba(10,10,10,0.35) 100%)',
          }}
          aria-hidden="true"
        />
        {/* subtle noise / vignette */}
        <div
          className="absolute inset-0 -z-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 20% 30%, rgba(239,106,31,0.10) 0%, transparent 55%)',
          }}
          aria-hidden="true"
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <SectionEyebrow>SaferPaws · Pet Safety, Reimagined</SectionEyebrow>
            <h1 className="mt-6 text-4xl sm:text-6xl lg:text-7xl font-heading font-black tracking-tighter leading-[0.98] text-white">
              Protect the Moments
              <br />
              That{' '}
              <span className="text-primary">Matter Most.</span>
            </h1>
            <div className="mt-8">
              <OrangeRule />
            </div>
            <p className="mt-8 text-lg sm:text-xl text-white/85 leading-relaxed max-w-xl">
              If your pet is ever lost, SaferPaws helps the person who finds
              them contact you instantly — giving your best friend the fastest
              possible route home.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/shop" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-8 py-7 font-semibold shadow-2xl shadow-primary/30 rounded-full"
                  data-testid="hero-protect-btn"
                >
                  <PawPrint className="mr-2 w-5 h-5" />
                  Protect My Pet
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/how-it-works" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto border-white/40 bg-white/5 hover:bg-white/10 text-white text-lg px-8 py-7 rounded-full backdrop-blur-sm"
                  data-testid="hero-see-how-btn"
                >
                  See How It Works
                </Button>
              </Link>
            </div>

            {!user && (
              <Link
                to="/login"
                className="mt-4 inline-block text-sm text-white/70 hover:text-primary transition-colors"
                data-testid="hero-login-link"
              >
                Already have an account? Log in →
              </Link>
            )}

            {/* Trust chips */}
            <div
              className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl"
              data-testid="trust-chips"
            >
              {trustChips.map((chip) => (
                <div
                  key={chip.label}
                  className="flex items-center gap-2.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm px-3 sm:px-4 py-2.5"
                >
                  <chip.icon className="w-4 h-4 text-primary shrink-0" strokeWidth={2} />
                  <span className="text-[11px] sm:text-xs font-medium text-white leading-tight">
                    {chip.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* WHY IT MATTERS — emotional reassurance                             */}
      {/* ================================================================== */}
      <section
        className="relative py-24 lg:py-32 bg-background"
        data-testid="why-matters-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
            <img
              src={IMG.emotional}
              alt="A pet owner walking their dog on a lead in warm evening light"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          </div>
          <div>
            <SectionEyebrow>Why It Matters</SectionEyebrow>
            <h2 className="mt-6 text-3xl sm:text-5xl font-heading font-bold tracking-tighter leading-[1.05]">
              A quiet gate. An open door. <br />
              <span className="text-primary">One unexpected moment.</span>
            </h2>
            <div className="mt-6"><OrangeRule /></div>
            <div className="mt-8 space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                Every pet owner knows the feeling. A door left ajar. A slipped
                lead. A firework at the wrong moment. In seconds, the pet you’ve
                loved for years is somewhere out there — and you’re not.
              </p>
              <p>
                SaferPaws isn’t about worst-case scenarios. It’s about the
                quiet confidence of knowing that if it ever happens, the person
                who finds your pet can reach you in a single tap.
              </p>
              <p className="text-foreground font-medium">
                One less thing to worry about, every single day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* HOW IT WORKS — 4 steps                                             */}
      {/* ================================================================== */}
      <section
        className="py-24 lg:py-32 bg-card/40 border-y border-border"
        data-testid="how-it-works-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <SectionEyebrow>How It Works</SectionEyebrow>
            <h2 className="mt-6 text-3xl sm:text-5xl font-heading font-bold tracking-tighter">
              Four seconds from{' '}
              <span className="text-primary">found</span> to home.
            </h2>
            <div className="mt-6"><OrangeRule /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, i) => (
              <div
                key={step.step}
                className="group relative bg-card rounded-2xl p-8 border border-border hover:border-primary/40 transition-all hover:-translate-y-1 duration-300"
                data-testid={`step-${step.step}`}
              >
                <div className="text-6xl font-heading font-black text-primary/20 group-hover:text-primary/40 transition-colors leading-none">
                  {step.step}
                </div>
                <h3 className="mt-4 text-xl font-heading font-semibold">
                  {step.title}
                </h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  {step.body}
                </p>
                {i < howItWorks.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute -right-3 top-1/2 w-6 h-6 text-primary/40" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* WHY SAFERPAWS — Benefits grid                                      */}
      {/* ================================================================== */}
      <section
        className="py-24 lg:py-32 bg-background"
        data-testid="benefits-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <SectionEyebrow>Why SaferPaws</SectionEyebrow>
            <h2 className="mt-6 text-3xl sm:text-5xl font-heading font-bold tracking-tighter">
              Built for real life,<br />
              not a spec sheet.
            </h2>
            <div className="mt-6"><OrangeRule /></div>
            <p className="mt-6 text-lg text-muted-foreground">
              Every feature exists for one reason: to give you calm when
              something unexpected happens.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div
                key={b.title}
                className="group bg-card rounded-2xl p-6 border border-border hover:border-primary/40 transition-colors"
                data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <b.icon className="w-6 h-6 text-primary" strokeWidth={2} />
                </div>
                <h3 className="mt-5 text-lg font-heading font-semibold">
                  {b.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* PRODUCT SHOWCASE                                                   */}
      {/* ================================================================== */}
      <section
        className="py-24 lg:py-32 bg-secondary/40 relative overflow-hidden"
        data-testid="product-showcase"
      >
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(circle at 70% 30%, rgba(239,106,31,0.12) 0%, transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <SectionEyebrow>The Tag</SectionEyebrow>
            <h2 className="mt-6 text-3xl sm:text-5xl font-heading font-bold tracking-tighter">
              Designed to be <span className="text-primary">seen</span>,<br />
              engineered to be <span className="text-primary">trusted</span>.
            </h2>
            <div className="mt-6"><OrangeRule /></div>
            <ul className="mt-10 space-y-5">
              {[
                { title: 'Premium polished metal finish', body: 'Feels solid in your hand, looks great on any collar.' },
                { title: 'Waterproof & shock-tested', body: 'Rain, mud, swimming, adventures — the tag keeps going.' },
                { title: 'NFC chip + printed QR code', body: 'Redundant technology so anyone can reach you, on any phone.' },
                { title: 'Silent — no jingle', body: 'Rounded, chip-free edges. Your pet won’t even notice it.' },
              ].map((f) => (
                <li key={f.title} className="flex gap-4">
                  <div className="w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="text-foreground font-semibold">{f.title}</p>
                    <p className="text-muted-foreground text-sm mt-0.5">{f.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/shop">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-6 text-base font-semibold"
                  data-testid="showcase-shop-btn"
                >
                  Shop the Tag
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Sparkles className="w-4 h-4 text-primary" />
                Free UK delivery on your first tag
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2 relative">
            <div
              className="absolute -inset-8 rounded-full bg-primary/10 blur-3xl -z-10"
              aria-hidden="true"
            />
            <img
              src={TAG_IMAGE}
              alt="SaferPaws polished metal pet tag with paw and heart emblem"
              className="w-full max-w-md mx-auto drop-shadow-2xl"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* COMPARISON TABLE                                                   */}
      {/* ================================================================== */}
      <section
        className="py-24 lg:py-32 bg-background"
        data-testid="comparison-section"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <SectionEyebrow>How It Compares</SectionEyebrow>
            <h2 className="mt-6 text-3xl sm:text-5xl font-heading font-bold tracking-tighter">
              Traditional Tag vs <span className="text-primary">SaferPaws</span>
            </h2>
            <div className="mt-6 mx-auto w-fit"><OrangeRule /></div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[2fr_1fr_1fr]">
              <div className="p-4 sm:p-5 border-b border-border bg-card/50">
                <span className="sr-only">Feature</span>
              </div>
              <div className="p-4 sm:p-5 border-b border-l border-border text-center bg-card/50">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Traditional</p>
                <p className="mt-1 text-sm font-heading font-semibold">Engraved Tag</p>
              </div>
              <div className="p-4 sm:p-5 border-b border-l border-border text-center bg-primary/10">
                <p className="text-xs uppercase tracking-widest text-primary font-semibold">SaferPaws</p>
                <p className="mt-1 text-sm font-heading font-semibold">Smart NFC Tag</p>
              </div>
              {comparison.map((row, i) => (
                <React.Fragment key={row.label}>
                  <div className={`p-4 sm:p-5 ${i < comparison.length - 1 ? 'border-b border-border' : ''} text-sm sm:text-base text-foreground`}>
                    {row.label}
                  </div>
                  <div className={`p-4 sm:p-5 border-l ${i < comparison.length - 1 ? 'border-b' : ''} border-border flex items-center justify-center`}>
                    {row.traditional ? (
                      <Check className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <X className="w-5 h-5 text-muted-foreground/40" />
                    )}
                  </div>
                  <div className={`p-4 sm:p-5 border-l ${i < comparison.length - 1 ? 'border-b' : ''} border-border flex items-center justify-center bg-primary/[0.04]`}>
                    {row.saferpaws ? (
                      <Check className="w-5 h-5 text-primary" strokeWidth={3} />
                    ) : (
                      <X className="w-5 h-5 text-muted-foreground/40" />
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* OUR STORY — founder                                                */}
      {/* ================================================================== */}
      <section
        className="py-24 lg:py-32 bg-card/40 border-y border-border"
        data-testid="story-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
            <img
              src={IMG.founder}
              alt="A husky sitting alertly, ears up"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <p className="text-sm uppercase tracking-widest opacity-80">The dog who started it all</p>
            </div>
          </div>
          <div>
            <SectionEyebrow>Our Story</SectionEyebrow>
            <h2 className="mt-6 text-3xl sm:text-5xl font-heading font-bold tracking-tighter leading-[1.05]">
              Why We Created<br />
              <span className="text-primary">SaferPaws.</span>
            </h2>
            <div className="mt-6"><OrangeRule /></div>
            <div className="mt-8 space-y-5 text-lg text-muted-foreground leading-relaxed">
              <p>
                My dad had a husky. Beautiful dog. Restless spirit. Every time
                someone accidentally left the gate open — she was gone.
              </p>
              <p>
                Most of the time she’d turn up at the local vet, a little muddy
                and very pleased with herself. But every escape came with the
                same knot in your stomach, the same rush to call around,
                the same hope that she’d be found by someone kind.
              </p>
              <p>
                It got us thinking. Not every dog is that lucky. Not every
                finder knows what to do. And when your pet is out there, you
                shouldn’t have to rely on hope.
              </p>
              <p className="text-foreground font-medium">
                SaferPaws exists so every pet has the best possible chance of
                finding their way home — and every owner has one less thing
                to worry about.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* COMMUNITY                                                          */}
      {/* ================================================================== */}
      <section
        className="py-24 lg:py-32 bg-background"
        data-testid="community-section"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionEyebrow>Our Vision</SectionEyebrow>
          <h2 className="mt-6 text-3xl sm:text-5xl font-heading font-bold tracking-tighter">
            A community that <br className="hidden sm:block" />
            <span className="text-primary">brings pets home.</span>
          </h2>
          <div className="mt-6 mx-auto w-fit"><OrangeRule /></div>
          <p className="mt-8 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            SaferPaws is more than a tag. We’re building a community of pet
            owners who look out for each other — where every finder becomes a
            hero and every reunion is a story worth telling.
          </p>
          <p className="mt-4 text-muted-foreground">
            We’re just getting started. Thank you for being part of the beginning.
          </p>
        </div>
      </section>

      {/* ================================================================== */}
      {/* REVIEWS                                                            */}
      {/* ================================================================== */}
      <section
        className="py-24 lg:py-32 bg-card/40 border-y border-border"
        data-testid="reviews-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <SectionEyebrow>Reviews & Reunions</SectionEyebrow>
            <h2 className="mt-6 text-3xl sm:text-5xl font-heading font-bold tracking-tighter">
              Stories from the <span className="text-primary">pack.</span>
            </h2>
            <div className="mt-6 mx-auto w-fit"><OrangeRule /></div>
          </div>

          {/* Empty-state placeholder card cluster — ready for real reviews */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className="bg-card rounded-2xl p-8 border border-border relative"
                data-testid={`review-placeholder-${n}`}
              >
                <Quote className="w-10 h-10 text-primary/30 absolute top-6 right-6" />
                <div className="flex gap-0.5 text-primary">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  Real customer stories will appear here once tags start
                  finding their way back home. Yours could be the first.
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <PawPrint className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Coming Soon</p>
                    <p className="text-xs text-muted-foreground">Verified Reunion</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/shop">
              <Button
                variant="outline"
                className="border-primary/40 bg-transparent text-primary hover:bg-primary/10 rounded-full px-6"
                data-testid="reviews-cta"
              >
                Be one of the first to share your story
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* FAQ                                                                */}
      {/* ================================================================== */}
      <section
        className="py-24 lg:py-32 bg-background"
        data-testid="faq-section"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <SectionEyebrow>Common Questions</SectionEyebrow>
            <h2 className="mt-6 text-3xl sm:text-5xl font-heading font-bold tracking-tighter">
              Everything you might <br />
              <span className="text-primary">want to know.</span>
            </h2>
            <div className="mt-6 mx-auto w-fit"><OrangeRule /></div>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`item-${i}`}
                className="bg-card rounded-2xl border border-border px-6 data-[state=open]:border-primary/40 transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-base sm:text-lg font-heading font-semibold hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ================================================================== */}
      {/* FINAL CTA                                                          */}
      {/* ================================================================== */}
      <section
        className="relative isolate py-32 lg:py-40 overflow-hidden"
        data-testid="final-cta-section"
      >
        <img
          src={IMG.finalCta}
          alt="A happy dog resting in soft daylight"
          className="absolute inset-0 -z-10 w-full h-full object-cover"
          loading="lazy"
        />
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,10,0.72) 0%, rgba(10,10,10,0.88) 100%)',
          }}
          aria-hidden="true"
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl sm:text-6xl lg:text-7xl font-heading font-black tracking-tighter leading-[1.02]">
            Give Your Pet the Best Chance <br className="hidden sm:block" />
            of <span className="text-primary">Finding Their Way Home.</span>
          </h2>
          <p className="mt-8 text-lg sm:text-xl text-white/85 max-w-2xl mx-auto leading-relaxed">
            One tap. One tag. One less thing to ever worry about.
          </p>
          <div className="mt-12">
            <Link to="/shop">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full text-lg px-10 py-7 font-semibold shadow-2xl shadow-primary/40"
                data-testid="final-cta-btn"
              >
                <PawPrint className="mr-2 w-5 h-5" />
                Protect My Pet Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
